import React, { useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { MaterialReactTable } from "material-react-table";
import { Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import TitlePages from "../../../layout/TitlePages";
import Breadcrumbs from "../../../layout/Breadcrumbs";
import Expandible from "../../../layout/HelperDataTable/Expandible";
import { FilterComponent, useCompleteTableConfig } from "../../../constants";
import { dataAcuerdos2026 } from "../../../data/2026/dataAcuerdos";

const baseUrl = "https://itetlax.org.mx/assets/pdf/acuerdos/ITE/2026/";

// Componente para el enlace al PDF
const PdfLink = ({ url }) => {
  const fullUrl = `${baseUrl}${url}`;
  return (
    <a href={fullUrl} target="_blank" rel="noreferrer">
      <FontAwesomeIcon icon={faFilePdf} className="btn btn-danger" />
    </a>
  );
};

// Fila de la tabla: usa inline style para diferenciar
const TableRow = ({ title, url, isSub }) => (
  <tr>
    <td style={{ fontWeight: isSub ? "normal" : "bold", fontStyle: isSub ? "italic" : "normal", paddingLeft: isSub ? "20px" : "0" }}>
      {isSub ? "└ " : ""}
      {title.toUpperCase()}
    </td>
    <td>
      <PdfLink url={url} />
    </td>
  </tr>
);

const AcuerdosITE2026 = () => {
  useEffect(() => {
    document.title = `Acuerdos ITE 2026`;
  }, []);

  const columnsAcuerdos = useMemo(
    () => [
      {
        accessorKey: "monthDoc",
        header: "MES",
        footer: "MES",
        size: 30,
        Filter: FilterComponent,
      },
      {
        accessorFn: (row) => `ITE-CG ${row.id}-2026`,
        id: "acuerdo",
        header: "ACUERDO",
        footer: "ACUERDO",
        size: 55,
      },
      {
        accessorFn: (row) =>
          row.nameDoc ? `${row.typeDoc} ${row.nameDoc}` : "",
        id: "titulo",
        header: "TÍTULO",
        footer: "TÍTULO",
        size: 255,
      },
    ],
    []
  );

  const renderDetailPanelAcuerdos = ({ row }) => {
    const anexoTitles = Object.keys(row.original).filter(
      (key) => key.startsWith("titleAnexo") && row.original[key]
    );

    return (
      <Box>
        <div className="table-responsive">
          <table className="table table-hover table-sm table-bordered table align-middle w-40">
            <thead>
              <tr>
                <td colSpan={2}>
                  <br />
                  <strong>{row.original.typeDoc.split("").join(" ")}</strong>
                  <br />
                  <br />
                </td>
              </tr>
            </thead>
            <tbody>
              {/* Acuerdo principal */}
              <tr className="table-secondary">
                <td>
                  {`${row.original.typeDoc} ITE-CG ${row.original.id}-2026 ${
                    row.original.nameDoc || ""
                  }`}
                </td>
                <td>
                  {row.original.id && (
                    <PdfLink url={row.original.id + ".pdf"} />
                  )}
                </td>
              </tr>

              {anexoTitles.length > 0 && (
                <tr>
                  <td colSpan={2}>
                    <br />
                    <strong>A N E X O</strong>
                    <br />
                    <br />
                  </td>
                </tr>
              )}

              {/* Renderizado de anexos y subanexos */}
              {(() => {
                const grouped = {};

                anexoTitles.forEach((key) => {
                  const index = key.replace("titleAnexo", "");
                  const [main, sub] = index.split("_");

                  if (!grouped[main]) grouped[main] = { main: null, subs: [] };

                  if (sub) grouped[main].subs.push({ key, index });
                  else grouped[main].main = { key, index };
                });

                return Object.keys(grouped).map((mainKey) => {
                  const group = grouped[mainKey];
                  return (
                    <React.Fragment key={mainKey}>
                      {group.main && (
                        <TableRow
                          title={row.original[group.main.key]}
                          url={`${row.original.id}.${group.main.index}.pdf`}
                          isSub={false}
                        />
                      )}
                      {group.subs.map((sub) => (
                        <TableRow
                          key={sub.index}
                          title={row.original[sub.key]}
                          url={`${row.original.id}.${sub.index}.pdf`}
                          isSub={true}
                        />
                      ))}
                    </React.Fragment>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </Box>
    );
  };

  const tableAcuerdos = useCompleteTableConfig(
    dataAcuerdos2026,
    columnsAcuerdos,
    renderDetailPanelAcuerdos
  );

  return (
    <div className="margin-bottom-2">
      <Breadcrumbs path={[{ label: `Acuerdos 2026` }]} />
      <TitlePages title="Acuerdos ITE" subTitle="Acuerdos ITE 2026" />
      <Expandible />
      <MaterialReactTable table={tableAcuerdos} />
    </div>
  );
};

export default AcuerdosITE2026;

// PropTypes
PdfLink.propTypes = {
  url: PropTypes.string.isRequired,
};

TableRow.propTypes = {
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  isSub: PropTypes.bool,
};